// @flow

import * as React from 'react';
import { Query } from 'react-apollo';
import { localize } from '@ta/common.i18n';
import type { SocialConnection } from '@ta/social.member-types';
import trackEventAJAX from '@ta/common.tracking';
import { SuggestedFolloweesLoadingSkeleton } from '@ta/social.skeletons';

import RecommendedConnectionsQuery from './RecommendedExternalConnections.gql';
import styles from './RecommendedExternalConnectionsDataProvider.less';

type Props = {
  /** children is a function that accepts a suggestedFriends render prop. */
  children: (suggestedFriends: SocialConnection[]) => React.Node,
  /** number of followees to fetch. */
  limit: number,
  /** offset to support pagination. */
  offset: number,
};

const maxRetries = 3;
const retryTimeout = 1000;
let currentRetries = 0;
let totalRetries = 0;

class RecommendedExternalConnectionsDataProvider extends React.Component<Props> {
  static defaultProps = {
    limit: 5,
    offset: 0,
  };
  render() {
    const ErrorScreen = ({ refetch }) => (
      <div className={styles.error}>
        {/* We had trouble finding your Facebook friends. */}
        {localize('onboarding_social_fbfollow_screen_header_error_cant_get_friends')}
        <div>
          <button
            className="ui_button standard"
            onClick={e => {
              e.preventDefault();
              refetch().then(() => this.forceUpdate());
            }}
          >
            {/* Try again */}
            {localize('onboarding_social_fbfollow_screen_header_error_try_again')}
          </button>
        </div>
      </div>
    );

    return (
      <Query
        errorPolicy="all"
        query={RecommendedConnectionsQuery}
        variables={{
          connectionType: 'FACEBOOK',
          limit: this.props.limit,
          offset: this.props.offset,
        }}
      >
        {({ data, error, loading, refetch }) => {
          if (loading) {
            return <SuggestedFolloweesLoadingSkeleton />;
          }
          if (error) {
            return <ErrorScreen refetch={refetch} />;
          }
          if (data && data.recommendedExternalConnectionsToFollow) {
            // If the query data shows 'PENDING', the friend data is not yet available.  Reattempt the query before showing an error screen.
            if (data.recommendedExternalConnectionsToFollow.status === 'PENDING') {
              if (currentRetries < maxRetries) {
                currentRetries++;
                totalRetries++;
                // Something buggy is going on with Apollo, and the query isn't being rerendered after the refetch call is completed.
                // this is a super-hack to force it to rerender when the query completes.  It probably shouldn't stay..
                // Also, the only reason this component is written as a class is to be able to call forceUpdate.
                setTimeout(() => {
                  refetch().then(() => this.forceUpdate());
                }, retryTimeout);
                return <SuggestedFolloweesLoadingSkeleton />;
              }
              currentRetries = 0;
              trackEventAJAX({
                module: 'external_connections_data_provider',
                action: 'pending_timeout',
                context: totalRetries.toString(),
              });
              return <ErrorScreen refetch={refetch} />;
            } else if (data.recommendedExternalConnectionsToFollow.status === 'FAILED') {
              trackEventAJAX({
                module: 'external_connections_data_provider',
                action: 'query_error',
                context: totalRetries.toString(),
              });
              return <ErrorScreen refetch={refetch} />;
            }
            if (data.recommendedExternalConnectionsToFollow.connections) {
              /* $FlowFixGraphql (WP-1357) */
              return this.props.children(data.recommendedExternalConnectionsToFollow.connections);
            }
          }

          return null;
        }}
      </Query>
    );
  }
}

export default RecommendedExternalConnectionsDataProvider;
